import { AfterViewInit, Component, DestroyRef, ElementRef, EventEmitter, Input, Output, ViewChild, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import { MatTooltipModule } from "@angular/material/tooltip";
import { NgxMasonryModule } from "ngx-masonry";
import { DebounceInputEventDirective } from "@app/shared/directives/debounce-input-event.directive";
import { InputTooltipDirective } from "@app/shared/directives/input-tooltip.directive";
import { CerrixIconViewerComponent } from "@app/shared/components/cerrix-icon-viewer/cerrix-icon-viewer.component";
import { DateTimeFormatPipe } from "@app/shared/pipes/date-time-format.pipe";
import { CerrixPromptComponent } from "@app/shared/cerrix-prompt/cerrix-prompt.component";
import { GenericListFieldType } from "@app/shared/models/GenericList/GenericListField";
import { CerrixPromptService } from "@app/shared/services/cerrix-prompt.service";
import { UserDetailModel } from "@app/user/user-maintenance/shared/user-detail-model";
import { ModuleType } from "@enums/ModuleType";
import { TabEventListenerType } from "@enums/TabEventListenerType.enum";
import { copyText, nameof } from "@methods/CommonMethods";
import { TabMenu } from "@models/generic/TabModels/TabMenu";
import { TabMenuItem } from "@models/generic/TabModels/TabMenuItem";
import { TabModel } from "@models/generic/TabModels/TabModel";
import { WorkspaceModulePermissionModel } from "@models/permissions/WorkspaceModulePermissions";
import { Preset, PresetConfig } from "@models/workspace/Preset";
import { SearchKeyword } from "@models/workspace/SearchKeyword";
import { WorkspacePresetService } from "@app/shared/services/workspace/workspace-preset.service";
import { TabService } from "@services/tabs/TabService";
import { ToastrService } from "ngx-toastr";
import { Pages } from "@constants/pages/Pages";
import { TranslatePipe, injectTranslation, provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope } from "./i18n/scope";
import { firstValueFrom, from } from "rxjs";

declare var $: any;

@Component({
    selector: "workspace-preset-management",
    templateUrl: "./workspace-preset-management.component.html",
    styleUrls: ["./workspace-preset-management.component.scss"],
    viewProviders: [
        provideTranslationScope(scope),
    ],
    imports: [
        FormsModule,
        DebounceInputEventDirective,
        InputTooltipDirective,
        NgxMasonryModule,
        CerrixIconViewerComponent,
        MatTooltipModule,
        TranslatePipe,
        DateTimeFormatPipe,
    ]
})
export class WorkspacePresetManagementComponent implements AfterViewInit {
    private _presetDS = inject(WorkspacePresetService);
    private _toastr = inject(ToastrService);
    private _promptService = inject(CerrixPromptService);
    private _tabService = inject(TabService);
    private _prompt = inject(CerrixPromptService);
    private _pages = inject(Pages);
    private _translation = injectTranslation(scope);
    private readonly _destroyRef = inject(DestroyRef);

    @ViewChild("allPresetsView", { static: true }) allPresetsView: ElementRef;

    @Input() cerrixTab: TabModel;
    @Input() moduleType: ModuleType;
    @Input() appendToModuleType: string;

    @Input() activeSearchKeywords: SearchKeyword[];
    @Input() allHeaders: string[];
    @Input() activeColumns: string[];
    @Input() activePaginationAmount: number;
    @Input() activeSortCol: string;
    @Input() sortAsc: boolean;

    @Input() permissions: WorkspaceModulePermissionModel;
    @Input() usePresetHyperlink: boolean = true;
    @Input() dataUiid: string = "undefined";

    @Output() presetsReady = new EventEmitter<boolean>();
    @Output() selectedPreset = new EventEmitter<PresetConfig>();
    // True when a deep-linked preset id could not be resolved (no access / not found), so the
    // center can show a message; reset to false whenever a preset is successfully selected.
    @Output() presetAccessDenied = new EventEmitter<boolean>();

    initialPresetsFetched = false;

    presets: PresetConfig[];
    scopedPresets: PresetConfig[];
    scopeFilter: string;
    customPresets: PresetConfig[];

    actualDefaultPresetID: number;
    managePresetID = 0;

    currentPreset: PresetConfig;

    createMenuItem: TabMenuItem = new TabMenuItem();
    browseMenu: TabMenuItem = new TabMenuItem();
    predefinedMenu: TabMenuItem = new TabMenuItem();
    favoriteMenu: TabMenuItem = new TabMenuItem();
    rolegroupMenu: TabMenuItem = new TabMenuItem();

    openPrompt: CerrixPromptComponent;
    user: UserDetailModel;

    disabledPresetChangeConfirmations = false;

    ngAfterViewInit() {
        this.setTabPresetChangeListener();

        // The sidebar menu, the prompts and the toasts are translated imperatively, before any
        // pipe in this scope renders. Wait for the scope first, then build the menu, so the
        // first render shows text instead of the raw keys.
        from(this._translation.ready())
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe(() => this.setupPresets());
    }

    private translate(key: string, params?: Record<string, unknown>): string {
        return this._translation.t(key, params);
    }

    private setTabPresetChangeListener() {
        this._tabService.listeners.removeTabListenerByType(
            this.cerrixTab,
            TabEventListenerType.LoadPreset
        );

        this._tabService.listeners.addGlobalListener(
            TabEventListenerType.LoadPreset,
            this.cerrixTab.lookupname,
            async (preset) => {
                if (preset) {
                    this.cerrixTab.activate();
                    if (!this.disabledPresetChangeConfirmations) {
                        // Another page can trigger this before the scope has loaded.
                        await this._translation.ready();
                        const result = await this._promptService
                            .confirmCustom({
                                maxHeight: "320px",
                                data: {
                                    title: this.translate("loadPrompt.title"),
                                    message: this.translate("loadPrompt.message"),
                                    fields: [
                                        {
                                            prettyName: this.translate(
                                                "loadPrompt.dontAskAgain"
                                            ),
                                            description: this.translate(
                                                "loadPrompt.dontAskAgainDescription"
                                            ),
                                            fieldName: "dontAskAgain",
                                            fieldType: GenericListFieldType.CheckBox,
                                        },
                                    ],
                                },
                            })
                            .toPromise();

                        if (!result) {
                            return;
                        } else if (result["dontAskAgain"]) {
                            this.disabledPresetChangeConfirmations = true;
                        }
                    }

                    this.setPreset(JSON.parse(preset));
                }
            },
            this.cerrixTab
        );
    }

    moduleCode(): string {
        let module = ModuleType[this.moduleType];
        if (this.appendToModuleType) {
            module += "-" + this.appendToModuleType;
        }

        return module;
    }

    fixIncorrectHeaderCasing(dbPresets: PresetConfig[]) {
        const presets: PresetConfig[] = [];
        dbPresets.forEach((preset) => {
            preset.preset.searchKeywords.forEach((y) => {
                this.allHeaders.forEach((header) => {
                    if (y.col.toLowerCase() === header.toLowerCase()) {
                        y.col = header;
                    }
                });
            });
            presets.push(preset);
        });

        return presets;
    }

    async setupPresets() {
        // bug 8351: Mailmessage menu not shown, because presets overrides the menu.
        if (!this.moduleType) {
            return;
        }

        let presets = await firstValueFrom(this._presetDS.getPresets(this.moduleCode()));
        presets = this.fixIncorrectHeaderCasing(presets);
        presets.forEach((preset) => {
            this.bindApi(preset);
        });

        this.presets = presets;
        this.customPresets = this.presets.filter((x) => !x.isPredefined);

        this.filterPresets();

        if (!this.initialPresetsFetched) {
            this.initialPresetsFetched = true;
            this.presetsReady.emit(true);

            // Resolve the default up front so setPreset can map default -> clean base URL even
            // when a non-default preset is deep-linked (activateDefaultPreset never runs then).
            this.actualDefaultPresetID = this.computeDefaultPresetId();

            // A deep link carries the preset either as ?activemenu=<id> (new query scheme) or as
            // the legacy /{page}/{id} path (tab.id). Converge both onto config.activemenu and
            // clear tab.id so the tab's query-less identity stays a stable /{page}. Deep linking
            // is a hyperlink feature; embedded workspaces (usePresetHyperlink=false) opt out so
            // their tab identity is left untouched.
            if (this.usePresetHyperlink && !this.cerrixTab.config.activemenu && this.cerrixTab.id) {
                this.cerrixTab.config.activemenu = this.cerrixTab.id;
                this.cerrixTab.id = undefined;
            }

            if (this.cerrixTab.config.presetOverride) {
                var presetConfig = this.fixIncorrectHeaderCasing([
                    JSON.parse(this.cerrixTab.config.presetOverride),
                ]).first();
                // One-shot input; drop it so buildTabPath does not serialize it into the URL.
                this.cerrixTab.config.presetOverride = null;
                this.setPreset(presetConfig);
            } else if (this.cerrixTab.config.presetID) {
                const presetToLoad = this.presets.find(
                    (x) => x.id == this.cerrixTab.config.presetID
                );
                this.cerrixTab.config.presetID = null;
                if (presetToLoad) {
                    this.setPreset(presetToLoad);
                } else {
                    this.activateDefaultPreset();
                }
            } else if (this.usePresetHyperlink && this.cerrixTab.config.activemenu) {
                const presetToLoad = this.presets.find(
                    (x) => x.id == this.cerrixTab.config.activemenu
                );
                if (presetToLoad) {
                    this.setPreset(presetToLoad);
                } else {
                    // Preset exists but the user cannot see it (or it is gone): show the center
                    // message and leave the sidebar usable; apply no preset.
                    this.presetAccessDenied.emit(true);
                }
            } else {
                this.activateDefaultPreset();
            }
        }

        this.createMenu();
    }

    private computeDefaultPresetId(): number {
        let presetToLoad = this.presets.filter((x) => x.isDefault === true)[0];
        if (!presetToLoad) {
            presetToLoad = this.presets.filter((x) => x.isSystemDefault === true)[0];
        }
        if (!presetToLoad) {
            presetToLoad = this.presets.filter((x) => x.isPredefined === true)[0];
        }

        return presetToLoad ? presetToLoad.id : null;
    }

    filterPresets() {
        if (!this.scopeFilter) {
            this.scopedPresets = this.presets;
            return;
        }

        const keywords = this.scopeFilter.toLowerCase().split(" ");
        const compare = (preset: PresetConfig, text: string) => {
            if (
                preset.name.toLowerCase().indexOf(text) >= 0 ||
                preset.description?.toLowerCase().indexOf(text) >= 0
            ) {
                return true;
            }

            return false;
        };

        this.scopedPresets = [];
        this.presets.forEach((preset) => {
            if (keywords.some((kw) => compare(preset, kw))) {
                this.scopedPresets.push(preset);
            }
        });
    }

    bindApi(preset: PresetConfig) {
        if (!preset.api) {
            preset.api = {};
        }

        preset.api.save = this.savePreset.bind(this, preset);
        preset.api.delete = this.delete.bind(this, preset);
        preset.api.toggleFavorite = this.toggleFavorite.bind(this, preset);
        preset.api.setDefault = this.updateDefaultPreset.bind(this, preset);
        preset.api.setSystemDefault = this.updateSystemDefaultPreset.bind(this, preset);
        preset.api.copyUrlToClipboard = this.copyUrlToClipboard.bind(this, preset);
    }

    copyUrlToClipboard(presetConfig: PresetConfig) {
        if (!presetConfig || !presetConfig.id || !this.cerrixTab.lookupname) {
            this._toastr.error(
                this.translate("toast.copyFailed"),
                this.translate("toast.errorTitle")
            );
            return;
        }

        const lookupKey = this._pages.getReversed(this.cerrixTab.lookupname);
        if (!lookupKey) {
            this._toastr.error(
                this.translate("toast.copyFailed"),
                this.translate("toast.errorTitle")
            );
            return;
        }

        let url = `${window.location.origin}/${lookupKey}?activemenu=${presetConfig.id}`;
        copyText(url.toLowerCase());
        this._toastr.success(
            this.translate("toast.urlCopied"),
            this.translate("toast.copyUrlTitle")
        );
    }

    savePreset(presetConfig: PresetConfig) {
        if (!presetConfig) {
            return;
        }

        presetConfig.module = this.moduleCode();
        const preset = new Preset();
        preset.columns = this.activeColumns;
        preset.paginationAmount = this.activePaginationAmount;
        preset.searchKeywords = this.activeSearchKeywords.filter((x) => x.remoteSearch !== true);
        preset.sortColumn = this.activeSortCol;
        preset.sortOrder = this.sortAsc ? "asc" : "desc";

        presetConfig.preset = preset;
        this._presetDS.store(presetConfig).subscribe(async (storedID: number) => {
            await this.setupPresets();

            const presetToLoad = this.presets.find((x) => x.id == storedID);
            if (presetToLoad) {
                this.setPreset(presetToLoad);
            }

            this._toastr.success(
                this.translate("toast.presetSaved", { name: presetConfig.name }),
                this.translate("toast.savedTitle")
            );
        });
    }

    async delete(preset: PresetConfig) {
        const shouldDelete = await this._prompt
            .confirm(
                this.translate("deletePrompt.title"),
                preset.isPublic
                    ? this.translate("deletePrompt.publicMessage")
                    : this.translate("deletePrompt.message")
            )
            .toPromise();

        if (!shouldDelete) {
            return false;
        }

        await firstValueFrom(this._presetDS.remove(preset.id));
        await this.setupPresets();
    }

    async toggleFavorite(preset: PresetConfig) {
        if (preset.isFavorite) {
            await firstValueFrom(this._presetDS.unfavorite(preset.id));
            preset.isFavorite = false;
            preset.isDefault = false;
        } else {
            await firstValueFrom(this._presetDS.favorite(preset.id));
            preset.isFavorite = true;
        }

        await this.setupPresets();
    }

    async togglePublic(preset: PresetConfig) {
        if (preset.isPublic) {
            await firstValueFrom(this._presetDS.unpublic(preset.id, this.moduleCode()));
            preset.isPublic = false;
            preset.isDefault = false;
        } else {
            await firstValueFrom(this._presetDS.public(preset.id, this.moduleCode()));
            preset.isPublic = true;
        }

        await this.setupPresets();
    }

    async deleteSelectedPreset() {
        await firstValueFrom(this._presetDS.remove(this.managePresetID));
        await this.setupPresets();
        this.managePresetID = 0;
    }

    updateDefaultPreset(preset: PresetConfig) {
        if (preset.id === 0) {
            return;
        }

        // If it was predefined but now it is custom, we should update predefined presets menu aswell.
        const isOrWasPredefined =
            this.presets.filter((x) => x.id === +preset.id || x.id === +this.actualDefaultPresetID)
                .length > 0;
        const isCustom = this.presets.filter((x) => x.id === +preset.id).length > 0;

        // we just want to know if it is an id that exists.
        if (isOrWasPredefined || isCustom) {
            this._presetDS.setDefault(preset.id, this.moduleCode()).subscribe((done) => {
                this.setupPresets();
            });
        }
    }

    updateSystemDefaultPreset(preset: PresetConfig) {
        if (preset.id === 0 || !this.permissions.canSetSystemDefault) {
            return;
        }

        this._presetDS.setSystemDefault(preset.id, this.moduleCode()).subscribe((done) => {
            this.setupPresets();
        });
    }

    activateDefaultPreset() {
        // Default precedence: personal default -> system default -> first predefined.
        this.actualDefaultPresetID = this.computeDefaultPresetId();

        if (this.actualDefaultPresetID != null) {
            const presetToLoad = this.presets.find((x) => x.id === this.actualDefaultPresetID);
            this.setPreset(presetToLoad);
        }
    }

    setPreset(presetConfig: PresetConfig) {
        this.openPrompt?.close();

        this.currentPreset = presetConfig;
        if (this.cerrixTab && this.cerrixTab.menu) {
            this.cerrixTab.menu.activeMenuItem = this.getPresetTabIdentifier(presetConfig.id);
        }

        // Reflect the active preset in the address bar (default preset -> clean base URL) and
        // clear any access-denied state. Embedded workspaces (usePresetHyperlink=false) opt out.
        if (this.usePresetHyperlink && this.cerrixTab && this.cerrixTab.config) {
            this.cerrixTab.config.activemenu =
                presetConfig.id === this.actualDefaultPresetID ? null : String(presetConfig.id);
            this._tabService.activeTabMenuChangedEvent.emit(this.cerrixTab.identifier);
            this.presetAccessDenied.emit(false);
        }

        this.selectedPreset.emit(presetConfig);
    }

    showEditPreset(preset: PresetConfig) {
        if (this.openPrompt) {
            this.openPrompt.close();
            this.openPrompt = null;
        }

        this.openPrompt = this._prompt.prompt({
            maxWidth: "700px",
            maxHeight: "550px",
            data: {
                title: this.translate("editDialog.title"),
                fields: [
                    {
                        fieldName: nameof<PresetConfig>("name"),
                        prettyName: this.translate("editDialog.name"),
                        fieldType: GenericListFieldType.Text,
                        required: true,
                        uiid: this.dataUiid + "-preset-settings-name-text",
                        editorWidth: 6,
                    },
                    {
                        fieldName: nameof<PresetConfig>("icon"),
                        prettyName: this.translate("editDialog.icon"),
                        fieldType: GenericListFieldType.IconPicker,
                        uiid: this.dataUiid + "-preset-settings-icon-picker",
                        editorWidth: 6,
                    },
                    {
                        fieldName: nameof<PresetConfig>("description"),
                        prettyName: this.translate("editDialog.description"),
                        fieldType: GenericListFieldType.TextArea,
                        uiid: this.dataUiid + "-preset-settings-description-textarea",
                    },
                    {
                        fieldName: nameof<PresetConfig>("roleGroups"),
                        prettyName: this.translate("editDialog.roleGroups"),
                        fieldType: GenericListFieldType.MultiSelect,
                        uiid: this.dataUiid + "-preset-settings-rolegroups-multiselect",
                        description: this.translate("editDialog.roleGroupsDescription"),
                        editorWidth: 12,
                        selfInit: true,
                        getDataMethod: () => {
                            return this._presetDS.getRoleGroups();
                        },
                    },
                    {
                        fieldName: nameof<PresetConfig>("isPublic"),
                        prettyName: this.translate("editDialog.isPublic"),
                        fieldType: GenericListFieldType.CheckBox,
                        uiid: this.dataUiid + "-preset-settings-ispublic-checkbox",
                        isReadonly: !this.permissions.canPublish || preset.isSystemDefault == true,
                        description: this.translate("editDialog.isPublicDescription"),
                        editorWidth: 6,
                    },
                    {
                        fieldName: nameof<PresetConfig>("isFavorite"),
                        prettyName: this.translate("editDialog.isFavorite"),
                        fieldType: GenericListFieldType.CheckBox,
                        uiid: this.dataUiid + "-preset-settings-isfavorite-checkbox",
                        isReadonly: preset.isDefault == true,
                        editorWidth: 6,
                    },
                ],
                fieldResult: preset,
                confirmOnEnter: false,
                confirmButton: {
                    uiid: this.dataUiid + "-preset-settings-confirm-button",
                    show: true,
                },
                cancelButton: {
                    uiid: this.dataUiid + "preset-settings-close-button",
                    text: this.translate("close"),
                },
            },
        });

        this.openPrompt.getResult().subscribe((preset: PresetConfig) => {
            this.savePreset(preset);
        });
    }

    updatePreset() {
        this.showEditPreset(this.currentPreset);
    }

    showBrowsePresets() {
        if (this.openPrompt) {
            this.openPrompt.close();
            this.openPrompt = null;
        }

        this.presets = null;
        this.setupPresets();

        this.openPrompt = this._prompt.prompt({
            maxWidth: "1200px",
            maxHeight: "800px",
            data: {
                title: this.translate("browse.title"),
                customTemplate: this.allPresetsView,
                confirmButton: {
                    show: false,
                },
                cancelButton: {
                    text: this.translate("close"),
                },
            },
        });
    }

    createMenu() {
        this.cerrixTab.menu?.menuItems.clear();

        if (!this.cerrixTab.menu) {
            this.cerrixTab.menu = new TabMenu();

            this.cerrixTab.menu.menuItemClicked = (tabMenuItem) => {
                const preset = this.presets.find((x) => x.id === tabMenuItem.obj);
                // Should only be one.
                if (preset) {
                    this.setPreset(preset);
                } else {
                    switch (tabMenuItem.identifier) {
                        case this.createMenuItem.identifier: {
                            const preset = new PresetConfig();
                            preset.isFavorite = true;

                            this.showEditPreset(preset);
                            break;
                        }
                        case this.browseMenu.identifier: {
                            this.showBrowsePresets();
                            break;
                        }
                    }
                }
            };

            this.createMenuItem = new TabMenuItem();
            this.createMenuItem.name = this.translate("menu.createPreset");
            this.createMenuItem.obj = "createMenuId";
            this.createMenuItem.iconClass = "fas fa-plus";
            this.createMenuItem.clickable = true;

            this.browseMenu = new TabMenuItem();
            this.browseMenu.name = this.translate("menu.browsePresets");
            this.browseMenu.obj = "browseMenuId";
            this.browseMenu.iconClass = "fas fa-folder-open";
            this.browseMenu.clickable = true;

            this.predefinedMenu = new TabMenuItem();
            this.predefinedMenu.name = this.translate("menu.predefinedPresets");
            this.predefinedMenu.iconClass = "far fa-dot-circle";
            this.predefinedMenu.clickable = false;

            this.favoriteMenu = new TabMenuItem();
            this.favoriteMenu.name = this.translate("menu.favorites");
            this.favoriteMenu.iconClass = "fas fa-star";
            this.favoriteMenu.clickable = false;

            this.rolegroupMenu = new TabMenuItem();
            this.rolegroupMenu.name = this.translate("menu.roleGroup");
            this.rolegroupMenu.iconClass = "fas fa-tags";
            this.rolegroupMenu.clickable = false;

            // Back/Forward to the clean base URL (no activemenu) re-selects the default preset.
            if (this.usePresetHyperlink) {
                this.cerrixTab.menu.selectDefaultMenuItem = () => this.activateDefaultPreset();
            }
        }

        this.cerrixTab.menu.menuItems.push(this.createMenuItem, this.browseMenu);

        this.cerrixTab.menu.menuItems.push(
            this.presets.some((x) => x.isFavorite) ? this.favoriteMenu : this.predefinedMenu
        );

        if (this.presets.some((x) => x.isRolegroup)) {
            this.cerrixTab.menu.menuItems.push(this.rolegroupMenu);
        }

        this.setPredefinedMenuItems();
        this.setFavoriteMenuItems();
        this.setRolegroupMenuItems();
    }

    setPredefinedMenuItems() {
        this.predefinedMenu.children = [];
        const predefined = this.presets.filter((x) => x.isPredefined);
        if (predefined.length > 0) {
            predefined.forEach((p) => {
                const pre = new TabMenuItem();
                pre.identifier = this.getPresetTabIdentifier(p.id);
                pre.menuItemId = String(p.id);
                pre.name = p.name;
                pre.obj = p.id;
                pre.iconClass = p.icon;

                if (this.currentPreset && p.id === this.currentPreset.id) {
                    this.cerrixTab.menu.activeMenuItem = pre.identifier;
                }

                this.predefinedMenu.children.push(pre);
            });
            this.predefinedMenu.hidden = false;
        } else {
            this.predefinedMenu.hidden = true;
        }
    }

    setFavoriteMenuItems() {
        this.favoriteMenu.children = [];
        const customs = this.presets.filter((x) => x.isFavorite);
        if (customs.length > 0) {
            customs.forEach((c) => {
                const custom = new TabMenuItem();
                custom.identifier = this.getPresetTabIdentifier(c.id);
                custom.menuItemId = String(c.id);
                custom.name = c.name;
                custom.obj = c.id;
                custom.iconClass = c.icon;

                if (this.currentPreset && c.id === this.currentPreset.id) {
                    this.cerrixTab.menu.activeMenuItem = custom.identifier;
                }
                this.favoriteMenu.children.push(custom);
            });
        }
    }

    OpenInfoDialog(preset: PresetConfig) {
        this._prompt.prompt({
            maxWidth: "600px",
            maxHeight: "525px",
            data: {
                title: preset.name,
                fields: [
                    {
                        fieldName: nameof<PresetConfig>("createdBy"),
                        prettyName: this.translate("infoDialog.createdBy"),
                        fieldType: GenericListFieldType.Text,
                        isReadonly: true,
                        uiid: this.dataUiid + "-createdBy-checkbox",
                        editorWidth: 12,
                    },
                    {
                        fieldName: nameof<PresetConfig>("updatedOn"),
                        prettyName: this.translate("infoDialog.lastUpdatedOn"),
                        fieldType: GenericListFieldType.DateTime,
                        isReadonly: true,
                        uiid: this.dataUiid + "-updatedOn-checkbox",
                        editorWidth: 12,
                    },
                    {
                        fieldName: nameof<PresetConfig>("isPredefined"),
                        prettyName: this.translate("infoDialog.isPredefined"),
                        fieldType: GenericListFieldType.CheckBox,
                        isReadonly: true,
                        uiid: this.dataUiid + "-isPredefined-checkbox",
                        editorWidth: 6,
                    },
                    {
                        fieldName: nameof<PresetConfig>("isSystemDefault"),
                        prettyName: this.translate("infoDialog.isSystemDefault"),
                        fieldType: GenericListFieldType.CheckBox,
                        isReadonly: true,
                        uiid: this.dataUiid + "-isSystemDefault-checkbox",
                        editorWidth: 6,
                    },
                    {
                        fieldName: nameof<PresetConfig>("isPublic"),
                        prettyName: this.translate("infoDialog.isPublic"),
                        fieldType: GenericListFieldType.CheckBox,
                        isReadonly: true,
                        uiid: this.dataUiid + "-isPublic-checkbox",
                        editorWidth: 6,
                    },
                    {
                        fieldName: nameof<PresetConfig>("isRolegroup"),
                        prettyName: this.translate("infoDialog.isRolegroup"),
                        fieldType: GenericListFieldType.CheckBox,
                        isReadonly: true,
                        uiid: this.dataUiid + "-isRolegroup-checkbox",
                        editorWidth: 6,
                    },
                    {
                        fieldName: nameof<PresetConfig>("isDefault"),
                        prettyName: this.translate("infoDialog.isDefault"),
                        fieldType: GenericListFieldType.CheckBox,
                        isReadonly: true,
                        uiid: this.dataUiid + "-isDefault-checkbox",
                        editorWidth: 6,
                    },
                    {
                        fieldName: nameof<PresetConfig>("isFavorite"),
                        prettyName: this.translate("infoDialog.isFavorite"),
                        fieldType: GenericListFieldType.CheckBox,
                        isReadonly: true,
                        uiid: this.dataUiid + "-isFavorite-checkbox",
                        editorWidth: 6,
                    },
                ],
                fieldResult: preset,
                confirmOnEnter: false,
                confirmButton: {
                    uiid: this.dataUiid + "-confirm-button",
                    show: false,
                },
                cancelButton: {
                    uiid: this.dataUiid + "-close-button",
                    text: this.translate("close"),
                },
            },
        });
    }

    setRolegroupMenuItems() {
        this.rolegroupMenu.children = [];
        const customs = this.presets.filter((x) => x.isRolegroup);
        if (customs.length > 0) {
            customs.forEach((c) => {
                const custom = new TabMenuItem();
                custom.identifier = this.getPresetTabIdentifier(c.id);
                custom.menuItemId = String(c.id);
                custom.name = c.name;
                custom.obj = c.id;
                custom.iconClass = c.icon;

                if (this.currentPreset && c.id === this.currentPreset.id) {
                    this.cerrixTab.menu.activeMenuItem = custom.identifier;
                }
                this.rolegroupMenu.children.push(custom);
            });
            this.rolegroupMenu.hidden = false;
        } else {
            this.rolegroupMenu.hidden = true;
        }
    }

    private getPresetTabIdentifier(presetID: number) {
        return "workspacepreset-" + this.moduleCode() + "-" + presetID;
    }
}
